/**
 * SageMaker IAM Setup Instructions
 *
 * Walks the user through creating the cross-account IAM role for SageMaker CloudWatch log access.
 */

import { useState } from "react";
import { Copy, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import type { SageMakerSetupInstructions as SageMakerSetupInstructionsType } from "@/types/sagemaker";

const INLINE_POLICY = JSON.stringify(
  {
    Version: "2012-10-17",
    Statement: [
      {
        Effect: "Allow",
        Action: [
          "logs:FilterLogEvents",
          "logs:DescribeLogGroups",
          "logs:DescribeLogStreams",
        ],
        Resource: "*",
      },
    ],
  },
  null,
  2
);

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={handleCopy}
      aria-label={`Copy ${label}`}
      className="h-7 px-2"
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-green-600" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
    </Button>
  );
}

interface SageMakerSetupInstructionsProps {
  setupInstructions: SageMakerSetupInstructionsType | undefined;
  isLoading: boolean;
}

export function SageMakerSetupInstructions({
  setupInstructions,
  isLoading,
}: SageMakerSetupInstructionsProps) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Generating your unique external ID…
      </div>
    );
  }

  if (!setupInstructions) return null;

  const trustPolicyJson = JSON.stringify(setupInstructions.trust_policy, null, 2);

  return (
    <div className="space-y-5 pt-3">
      <p className="text-sm text-muted-foreground">
        Follow these steps in your AWS account to create the IAM role Vortal will use to read
        your SageMaker CloudWatch logs.
      </p>

      <div className="space-y-3">
        <p className="text-sm font-medium">
          1. Go to <strong>AWS Console → IAM → Roles → Create role</strong>
        </p>
        <p className="text-sm font-medium">
          2. Select <strong>Custom trust policy</strong> and paste the JSON below, then click{" "}
          <strong>Next</strong>
        </p>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Trust Policy
            </Label>
            <CopyButton value={trustPolicyJson} label="trust policy" />
          </div>
          <pre className="rounded-md bg-background border px-3 py-2 text-xs font-mono overflow-x-auto max-h-44">
            {trustPolicyJson}
          </pre>
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              External ID (already included above)
            </Label>
            <CopyButton value={setupInstructions.external_id} label="external ID" />
          </div>
          <div className="rounded-md bg-background border px-3 py-2 font-mono text-sm select-all">
            {setupInstructions.external_id}
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <p className="text-sm font-medium">
          3. On the <strong>Add permissions</strong> step —{" "}
          <span className="text-muted-foreground">skip it</span>, just click <strong>Next</strong>.
          The permissions needed are added as a custom inline policy in the next step.
        </p>
        <p className="text-sm font-medium">
          4. Give the role a name (e.g.{" "}
          <code className="text-xs bg-muted px-1 py-0.5 rounded">VortalSageMakerRole</code>) and
          click <strong>Create role</strong>
        </p>
      </div>

      <div className="space-y-3">
        <p className="text-sm font-medium">
          5. Open the role you just created → <strong>Permissions</strong> tab →{" "}
          <strong>Add permissions → Create inline policy</strong>
        </p>
        <p className="text-sm font-medium">
          6. Switch to the <strong>JSON</strong> tab, paste the policy below, click{" "}
          <strong>Next</strong>, name it (e.g.{" "}
          <code className="text-xs bg-muted px-1 py-0.5 rounded">VortalCloudWatchLogsRead</code>
          ), then <strong>Create policy</strong>
        </p>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Inline Policy (CloudWatch Logs read access)
            </Label>
            <CopyButton value={INLINE_POLICY} label="inline policy" />
          </div>
          <pre className="rounded-md bg-background border px-3 py-2 text-xs font-mono overflow-x-auto max-h-44">
            {INLINE_POLICY}
          </pre>
        </div>
      </div>

      <div className="space-y-1">
        <p className="text-sm font-medium">
          7. Copy the role's <strong>ARN</strong> from the role summary page and paste it into the
          form below.
        </p>
        <p className="text-xs text-muted-foreground">
          The ARN looks like:{" "}
          <code className="bg-muted px-1 py-0.5 rounded">
            arn:aws:iam::123456789012:role/VortalSageMakerRole
          </code>
        </p>
      </div>
    </div>
  );
}
